/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useEffect,
  useRef,
  useState,
} from 'react';

import Lottie from 'lottie-react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import paymentAnimation from '../../../_metronic/assets/animations/payment.json';
import paymentRejectedAnimation from '../../../_metronic/assets/animations/paymentRejected.json';
import LoadingContent from '../../modules/loading/LoadingContent';
import LoadingBranch from '../../modules/loading/LoadingBranch';

import SupportOption from 'app/pages/HelpCenter/SupportOption';
import CancelModal from 'app/utils/CancelModal';

import {
  formatLongDateToDate,
  formatLongDateTime,
  convertWeekday,
} from 'app/services/dates';

import {
  formatMoney,
} from 'app/services/intFormat';

import {
  fetchContract,
  fetchCancelContract,
} from 'app/services/contracts';

import {
  fetchNextLessons,
  getLessonPath,
} from 'app/services/lessons';

import LessonItem from 'app/pages/Lessons/LessonItem';

type ContractProps = {
  basePath: string,
}

const Contract = ({ basePath }: ContractProps) => {
  const { auth, contracts } = useSelector((state : any) => ({
    auth: state.auth,
    contracts: state.contracts,
  }), shallowEqual);

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [contract, setContract] = useState<any>({});
  const [invoice, setInvoice] = useState<any>({});
  const [invoices, setInvoices] = useState<any>([]);
  const [openCancelContract, setOpenCancelContract] = useState<boolean>(false);
  const [lessons, setLessons] = useState<any>([]);

  const lottieRef = useRef<any>();

  useEffect(() => {
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({ type: 'app-modal-open' }))
    }
    return () => {
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({ type: 'app-modal-close' }))
      }
    }
  }, []);

  useEffect(() => {
    setContract({});
    setInvoices([]);
    setInvoice({});

    getContract();
  }, []);

  useEffect(() => {
    if (contract?.id) {
      let currentInvoice: any = {};

      if (contract.relationships?.invoices?.length === 1) {
        currentInvoice = contract.relationships.invoices[0];
      } else if (contract.relationships?.invoices?.length > 1) {
        currentInvoice = contract.relationships.invoices.find((value: any) => value.attributes.is_current === true) || contract.relationships.invoices[0];
      }

      setInvoice(currentInvoice);
      setInvoices(contract.relationships?.invoices ?? []);
      getNextLessons();
    }
  }, [contract]);

  // next occurrences of the classes of the contract (one schedule per weekday)
  const getNextLessons = () => {
    if (contract?.relationships?.service?.attributes?.modality !== 'class' || !contract?.attributes?.is_active) {
      setLessons([]);
      return;
    }

    const scheduleIds: string[] = Array.from(new Set(
      (contract.relationships?.schedule_spot ?? []).map((spot: any) => spot.relationships?.schedule?.id).filter(Boolean)
    ));

    Promise.all(scheduleIds.map((scheduleId) => fetchNextLessons(scheduleId).catch(() => [])))
      .then((results: any) => {
        setLessons(results.flat()
          .filter((lesson: any) => lesson.attributes.is_participant)
          .sort((a: any, b: any) => a.attributes.start_at.localeCompare(b.attributes.start_at)));
      });
  }

  useEffect(() => {
    if (!auth.user) {
      navigate('/')
    }
  }, [auth]);

  const getContract = () => {
    dispatch<any>(fetchContract(id)).then((response: any) => {
      setContract(response);
    }).catch(() => {
      navigate(basePath);
    });
  }

  const cancel = () => {
    dispatch<any>(fetchCancelContract(contract.id)).then(() => {
      setOpenCancelContract(false);
      getContract();
    }).catch(() => {
      setOpenCancelContract(false);
    });
  }

  const getBranch = () => {
    return contract?.relationships?.schedule_spot?.[0]?.relationships?.schedule?.relationships?.field?.relationships?.branch;
  }

  const getModalityLabel = (plural = true) => {
    if (contract?.relationships?.service?.attributes?.modality === 'class') {
      return plural ? 'aulas' : 'aula';
    }

    return plural ? 'locações' : 'locação';
  }

  const getPlanLabel = (value: any) => {
    if (value === 'quarterly') {
      return 'Trimestral';
    }
    if (value === 'semester') {
      return 'Semestral';
    }
    if (value === 'yearly') {
      return 'Anual';
    }

    return 'Mensal';
  }

  const getStatus = () => {
    if (invoice?.attributes?.is_paid) {
      return 'Pago';
    }

    if (invoice?.attributes?.is_canceled) {
      return 'Cancelado';
    }

    if (invoice?.attributes?.is_expired) {
      return 'Vencido';
    }

    return 'Não pago';
  }

  return (
    <>
      <div id='kt_app_content' className='flex-column-fluid'>
        <span
          className='btn btn-md btn-icon btn-bg-light btn-active-color-primary w-100px h-40px cursor-pointer mb-5'
          onClick={() => navigate(basePath)}
        >
          <i className='las la-arrow-left fw-bold fs-2 text-primary me-3' />
          Voltar
        </span>

        <div className='d-flex flex-column flex-lg-row'>
          <div className='flex-lg-row-fluid mb-5 mb-lg-0'>
            {lessons.length > 0 && <div className='card mb-5'>
              <div className='card-body pt-5'>
                <div className='justify-content-space-between d-flex mb-2'>
                  <span className='flex-root text-center fs-2 fw-bold'>
                    Próximas aulas
                  </span>
                </div>
                <div className='text-center text-muted fw-semibold fs-6 mb-8'>
                  Não vai conseguir ir? Marque falta e ganhe um crédito de reposição válido por 30 dias.
                </div>

                <div className='row'>
                  <div className='col-md-10 col-lg-10 offset-lg-1 offset-md-1'>
                    {lessons.map((lesson: any) => <LessonItem
                      key={`lesson-${lesson.id}-${lesson.attributes.start_at}`}
                      lesson={lesson}
                      showDate
                      onClick={() => navigate(getLessonPath(lesson.id, lesson.attributes.date))}
                    />)}
                  </div>
                </div>
              </div>
            </div>}

            <div className='card mb-5 mb-sm-0'>
              <div className='card-body pt-5'>
                <div className='justify-content-space-between d-flex mb-5'>
                  <span className='flex-root text-center fs-2 fw-bold'>
                    Detalhes da assinatura
                  </span>
                </div>

                <div className='row mt-10'>
                  {contract?.attributes?.is_active && <div className='col-md-8 col-lg-6 mb-5 mb-md-0 offset-lg-3 offset-md-2'>
                    <div className='flex-root justify-content-center text-center'>
                      <div className='card border border-1 border-gray-200 bg-gray-300 w-100'>
                        <div className='d-flex flex-stack justify-content-center text-center me-2'>
                          <Lottie
                            style={{
                              width: 50,
                              height: 50,
                            }}
                            animationData={paymentAnimation}
                            loop={false}
                            autoplay
                            rendererSettings={{
                              preserveAspectRatio: 'xMidYMid slice',
                            }}
                            initialSegment={[0, 22]}
                          />
                          <div className='fs-5 opacity-75 fw-bold px-3'>
                            Assinatura ativa
                          </div>
                        </div>
                      </div>
                      <div className='fs-6 opacity-50 mt-5'>
                        Sua assinatura está ativa, bora jogar!
                      </div>
                    </div>
                  </div>}

                  {contract?.id && !contract?.attributes?.is_active && <div className='col-md-8 col-lg-6 mb-5 mb-md-0 offset-lg-3 offset-md-2'>
                    <div className='flex-root justify-content-center text-center'>
                      <div className='card border border-1 border-gray-200 bg-gray-300 p-3 w-100'>
                        <div className='d-flex flex-stack justify-content-center text-center me-2'>
                          <Lottie
                            style={{
                              width: 30,
                              height: 30,
                            }}
                            lottieRef={lottieRef}
                            animationData={paymentRejectedAnimation}
                            loop={false}
                            onDOMLoaded={() => lottieRef.current?.setSpeed(1.4)}
                            autoplay
                          />
                          <div className='fs-5 opacity-75 fw-bold px-3'>
                            Assinatura finalizada
                          </div>
                        </div>
                      </div>
                      <div className='fs-6 opacity-50 mt-5'>
                        Você ainda pode ver as informações da sua assinatura e suas faturas
                      </div>
                    </div>
                  </div>}
                </div>

                {contract?.id && <div className='row mt-5'>
                  <div className='col-md-10 col-lg-10 mb-5 mb-md-0 offset-lg-1 offset-md-1'>
                    <div className='justify-content-space-between d-flex mb-2'>
                      <span className='flex-root fs-3 fw-bold text-primary'>
                        Informações
                      </span>
                    </div>

                    <div className='table-responsive'>
                      <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
                        <thead>
                          <tr className='border-0'>
                            <th className='p-0 w-140px'></th>
                            <th className='p-0 min-w-140px'></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Início da assinatura
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {formatLongDateToDate(contract.attributes.start_at)}
                            </td>
                          </tr>
                          {contract.attributes.finish_at && <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Término da assinatura
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {formatLongDateToDate(contract.attributes.finish_at, true)}
                            </td>
                          </tr>}
                          <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Unidade
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {getBranch()?.attributes?.name}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Quantidade de {getModalityLabel(true)}
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {contract?.relationships?.service?.attributes?.recurrence_times} por semana
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>}

                {contract?.id && contract?.relationships?.schedule_spot?.length > 0 && <div className='row mt-5'>
                  <div className='col-md-10 col-lg-10 mb-5 mb-md-0 offset-lg-1 offset-md-1'>
                    <div className='justify-content-space-between d-flex mb-2'>
                      <span className='flex-root fs-3 fw-bold text-primary'>
                        Horário das {getModalityLabel(true)}
                      </span>
                    </div>

                    {contract.relationships.schedule_spot.map((spot: any) => <div
                      key={`schedule-spot-${spot.id}`}
                      className='table-responsive'
                    >
                      <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
                        <thead>
                          <tr className='border-0'>
                            <th className='p-0 w-140px'></th>
                            <th className='p-0 min-w-140px'></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Quando
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {convertWeekday(spot.relationships.schedule.attributes.recurrence_days_bit_mask)} às {formatLongDateTime(spot.relationships.schedule.attributes.start_at)?.split('•')[1]?.trim()}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Quadra
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {spot.relationships.schedule.relationships.field.attributes.name}
                            </td>
                          </tr>
                          {spot.relationships.schedule.relationships.participants?.length > 0 && <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Professor
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {spot.relationships.schedule.relationships.participants.map((participant: any) => <div key={`participant-${participant.id}`}>
                                {participant.attributes.name}
                              </div>)}
                            </td>
                          </tr>}
                        </tbody>
                      </table>
                    </div>)}
                  </div>
                </div>}

                {contract?.id && <div className='row mt-5'>
                  <div className='col-md-10 col-lg-10 mb-5 mb-md-0 offset-lg-1 offset-md-1'>
                    <div className='justify-content-space-between d-flex mb-2'>
                      <span className='flex-root fs-3 fw-bold text-primary'>
                        Resumo de valores
                      </span>
                    </div>
                    <div className='table-responsive'>
                      <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
                        <thead>
                          <tr className='border-0'>
                            <th className='p-0 w-140px'></th>
                            <th className='p-0 min-w-140px'></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Serviço
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {contract?.relationships?.service?.attributes?.name}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Pagamento
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {getPlanLabel(contract.attributes.plan)}
                            </td>
                          </tr>
                          {invoice?.attributes && contract?.attributes?.is_active && <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Vencimento da fatura
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {formatLongDateToDate(invoice.attributes.expired_at)}
                            </td>
                          </tr>}
                          {invoice?.attributes && contract?.attributes?.is_active && <tr>
                            <td>
                              <span className='text-gray-700 fw-bold mb-1 fs-6'>
                                Status da fatura
                              </span>
                            </td>
                            <td className='text-end text-dark fw-semibold'>
                              {getStatus()}
                            </td>
                          </tr>}
                          <tr>
                            <td>
                              <span className='fs-5 text-white fw-bold mb-1'>
                                Valor do plano
                              </span>
                            </td>
                            <td className='fs-5 text-end text-primary fw-bold'>
                              <span>
                                {formatMoney(contract.attributes.price)}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>}

                {contract?.id && <div className='row mt-5'>
                  <div className='col-md-10 col-lg-10 mb-5 mb-md-0 offset-lg-1 offset-md-1'>
                    <div className='justify-content-space-between d-flex mb-2'>
                      <span className='flex-root fs-3 fw-bold text-primary'>
                        Precisa de ajuda?
                      </span>
                    </div>
                    <div className='table-responsive'>
                      <SupportOption
                        originType={'contract'}
                        originId={contract.id}
                        canCancel={contract?.attributes?.can_cancel === true && contract?.attributes?.is_active}
                        cancelTitle={'Quero cancelar minha assinatura'}
                        phone={getBranch()?.attributes?.phone}
                        cancel={() => setOpenCancelContract(true)}
                      />
                    </div>
                  </div>
                </div>}

                {contracts.listLoading === false && !contract?.id && <div className='mb-13 text-center'>
                  <h2 className='mb-3'>
                    Ops, assinatura não encontrada!
                  </h2>
                  <div className='text-muted fw-semibold fs-5'>
                    Não encontramos a página que você procura!
                  </div>
                  <div className='text-muted fw-semibold fs-5'>
                    Verifique a url ou tente novamente.
                  </div>
                </div>}

                {contracts.listLoading === true && !contract?.id && <LoadingContent />}
              </div>
            </div>
          </div>

          <div className='flex-lg-auto min-w-lg-400px ms-lg-10'>
            {contract?.id && invoices.length > 0 && <div className='card mb-5'>
              <div className='card-body pt-5 pb-0 mb-5'>
                <div className='mb-5 text-center'>
                  <span className='flex-root text-center fs-2 fw-bold mb-3'>
                    Faturas
                  </span>
                </div>

                <div className='row mt-5'>
                  <div className='col-lg-12'>
                    <div className='table-responsive'>
                      <table className='table table-row-gray-200 align-middle gs-0 gy-4'>
                        <thead>
                          <tr className='border-0'>
                            <th className='p-0 w-140px'></th>
                            <th className='p-0 min-w-140px'></th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoiceItem: any, index: number) => <div key={`invoice-${invoiceItem.id}`}>
                            <tr className='my-0 py-0'>
                              <td className='my-0 py-0'>
                                <span className='d-flex flex-row justify-content-between mb-1 fs-6'>
                                  <span className='text-muted fw-bold'>Vigência</span>
                                  <span className='d-flex flex-column text-dark fw-semibold'>
                                    {invoiceItem.attributes.start_at === invoiceItem.attributes.finish_at && <span className='d-flex justify-content-end'>
                                      {formatLongDateToDate(invoiceItem.attributes.finish_at)}
                                    </span>}

                                    {invoiceItem.attributes.start_at !== invoiceItem.attributes.finish_at && <>
                                      <span className='d-flex justify-content-end'>
                                        De {formatLongDateToDate(invoiceItem.attributes.start_at)}
                                      </span>
                                      <span className='d-flex justify-content-end'>
                                        Até {formatLongDateToDate(invoiceItem.attributes.finish_at)}
                                      </span>
                                    </>}
                                  </span>
                                </span>
                              </td>
                            </tr>
                            <tr className='my-0 py-0'>
                              <td className='my-0 py-0'>
                                <span className='d-flex flex-row justify-content-between mb-1 fs-6'>
                                  <span className='text-muted fw-bold'>Valor</span>
                                  <span className='text-dark fw-semibold'>
                                    {formatMoney(invoiceItem?.attributes?.price)}
                                  </span>
                                </span>
                              </td>
                            </tr>
                            <tr className='my-0 py-0'>
                              <td className='my-0 py-0'>
                                <span className='d-flex flex-row justify-content-between mb-1 fs-6'>
                                  <span className='text-muted fw-bold'>Situação</span>
                                  <span className='text-dark fw-semibold'>
                                    {invoiceItem.attributes.is_pending && <span className='white'>A vencer</span>}
                                    {invoiceItem.attributes.is_paid && <span className='text-primary'>Pago</span>}
                                    {invoiceItem.attributes.is_canceled && <span className='text-gray-500'>Cancelado</span>}
                                    {invoiceItem.attributes.is_expired && <span className='text-danger'>Vencido</span>}
                                  </span>
                                </span>
                              </td>
                            </tr>
                            <tr className='my-0 py-0'>
                              <td className='text-end my-0 py-0'>
                                <span
                                  className='btn btn-bg-light btn-active-color-primary px-0 py-2 w-100px align-self-end text-white'
                                  onClick={() => navigate(`/faturas/${invoiceItem.id}`)}
                                >
                                  Ver fatura
                                </span>
                              </td>
                            </tr>
                            {invoices.length !== (index + 1) && <div className='separator separator-dashed my-3'></div>}
                          </div>)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {contracts.listLoading === true && !contract?.id && <LoadingBranch />}
              </div>
            </div>}
          </div>
        </div>
      </div>

      <CancelModal
        isOpen={openCancelContract}
        onClosed={() => setOpenCancelContract(false)}
        title='Cancelar assinatura'
        text={contract?.attributes?.can_cancel ? <span className='text-gray-800 fw-light'>
          Seu agendamento será cancelado, e o horário será liberado para agendamento.<br />
          Não será possível reativar sua assinatura após o cancelamento.
        </span> : <span className='text-gray-800 fw-light'>
          Não é possível realizar o cancelamento<br />da sua assinatura de forma automática.<br />
          É necessário que entre em contato com a unidade e solicite o cancelamento, você também pode entrar em contato com nosso suporte para mais informações.
        </span>}
        canCancel={contract?.attributes?.can_cancel === true}
        button='Cancelar assinatura'
        onChange={() => cancel()}
        isLoading={contracts.actionsLoading === true}
      />
    </>
  );
}

export {
  Contract,
};
